// Detect TransferPitch player / coach profile URLs in arbitrary message
// text and report them as structured matches so the chat renderer can
// stack profile cards under the bubble.
//
// Three URL flavours are recognised:
//
//   1. Absolute URLs that point at our own apex / staging / dashboard
//      hosts, e.g.
//      https://transferpitch.com/search/player/12345
//      https://www.transferpitch.com/search/coach/678
//      https://staging.transferpitch.com/search/player/9
//      https://dashboard.transferpitch.com/search/player/42
//
//   2. localhost development URLs on any port, e.g.
//      http://localhost:3000/search/player/3322
//      http://localhost:5173/search/coach/7
//
//   3. Relative paths users sometimes paste from the address bar:
//      /search/player/12345
//      /search/coach/678
//
// Anything else — google.com, youtube embeds, plain text — flows through
// the default Stream Chat rendering untouched. We deliberately do NOT
// rewrite the message text; the renderer overlays cards as siblings.

// transferpitch.com with optional www / staging / dashboard subdomain
const ABS_TP_PATTERN =
  /https?:\/\/(?:www\.|staging\.|dashboard\.)?transferpitch\.com\/search\/(player|coach)\/(\w+)/gi;
// Local dev host on any port (3000, 5173, etc.)
const ABS_LOCAL_PATTERN =
  /https?:\/\/localhost(?::\d+)?\/search\/(player|coach)\/(\w+)/gi;
// Relative paths
const REL_PATTERN = /(?<![\w/])\/search\/(player|coach)\/(\w+)(?![\w/])/gi;

// Host-only match — does this URL point at ANY TransferPitch host?
// Used to suppress Stream Chat's generic OG previews when the message
// already contains a profile link (Stream's bot can normalize the
// scraped URL to the SPA's homepage og:url, so the attachment ends up
// showing the generic marketplace banner instead of the profile —
// duplicative and noisy).
const TP_HOST_PATTERN =
  /^https?:\/\/(?:www\.|staging\.|dashboard\.)?transferpitch\.com(?:\/|$|\?|#)|^https?:\/\/localhost(?::\d+)?(?:\/|$|\?|#)/i;

export function isTransferPitchHost(url) {
  if (!url || typeof url !== "string") return false;
  return TP_HOST_PATTERN.test(url);
}

/**
 * @typedef {Object} ProfileLinkMatch
 * @property {"player"|"coach"} type
 * @property {string} id         IDs as written by the user; could be numeric
 *                                or the Wyscout/TS UUID — backend looks both up.
 * @property {string} fullUrl    Exact substring matched in the message text.
 * @property {number} index      Position in the source string (for ordering).
 */

/**
 * Find all TransferPitch profile links in a string. De-duplicates by
 * (type, id) — the same profile mentioned twice yields one card.
 *
 * @param {string} text
 * @returns {ProfileLinkMatch[]}
 */
export function detectProfileLinks(text) {
  if (!text || typeof text !== "string") return [];

  const seen = new Set();
  const out = [];

  for (const re of [ABS_TP_PATTERN, ABS_LOCAL_PATTERN, REL_PATTERN]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      const type = m[1].toLowerCase(); // "player" | "coach"
      const id = m[2];
      const key = `${type}:${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        type,
        id,
        fullUrl: m[0],
        index: m.index,
      });
    }
  }

  // Preserve source order — useful when one message mentions several
  // profiles ("compare @ronaldo with @endrick").
  out.sort((a, b) => a.index - b.index);
  return out;
}

/**
 * Convenience: just the boolean "does this message contain at least one
 * profile link?" — lets the renderer short-circuit without an array
 * allocation when 99% of messages are plain text.
 */
export function hasProfileLink(text) {
  if (!text || typeof text !== "string") return false;
  // Cheap substring check first; falls back to the full regex only when
  // the keyword is present at all.
  if (!text.includes("/search/")) return false;
  ABS_TP_PATTERN.lastIndex = 0;
  ABS_LOCAL_PATTERN.lastIndex = 0;
  REL_PATTERN.lastIndex = 0;
  return (
    ABS_TP_PATTERN.test(text) ||
    ABS_LOCAL_PATTERN.test(text) ||
    REL_PATTERN.test(text)
  );
}

/**
 * Strict check for a single URL string — "is this URL a TransferPitch
 * player/coach profile link?". Used by the chat attachment renderer
 * to drop Stream's default OG link-preview when we already render a
 * native ProfileCard for the same URL. Returns false for null/undefined
 * input, plain text, or any unrelated URL.
 */
export function isProfileUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (!url.includes("/search/")) return false;
  // Reset state because the global flag mutates lastIndex.
  ABS_TP_PATTERN.lastIndex = 0;
  ABS_LOCAL_PATTERN.lastIndex = 0;
  REL_PATTERN.lastIndex = 0;
  return (
    ABS_TP_PATTERN.test(url) ||
    ABS_LOCAL_PATTERN.test(url) ||
    REL_PATTERN.test(url)
  );
}
